import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  boolBaseStatusOptions,
  occupancyWithSecondaryResidenceOptions,
  titleHoldingOptions,
} from 'constants/options';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../../Borrowers.testIds';
import { defaultRadioInputProps, getAdditionalContentProps } from '../Declarations.constants';
import { sectionStyles, sectionTitleStyles } from '../Declarations.styles';

export const getConfigFields = ({ entityId, isBorrowerSectionWasOpened }) => {
  const modelPath = `[${entityId}].declarations.propertyAndLoan.`;

  return {
    isFirstTimeHomeBuyer: {
      fieldId: 'isFirstTimeHomeBuyer',
      type: FieldType.SELECT,
      nameInModel: `[${entityId}].isFirstTimeHomeBuyer`,
      inputProps: {
        disabled: true,
        containerClassName: 'tool__visually-hidden',
        options: boolBaseStatusOptions,
      },
    },
    intendToOccupyPropertyAsPrimaryResidence: {
      fieldId: 'intendToOccupyPropertyAsPrimaryResidence',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}intendToOccupyPropertyAsPrimaryResidence`,
      inputProps: {
        label: 'Will you occupy the property as your primary residence?',
        dataTestId:
          BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_INTENDTOOCCUPYPROPERTYASPRIMARYRESIDENCE_SELECT,
        ...defaultRadioInputProps,
        ...getConditionalObject(!isBorrowerSectionWasOpened, {
          customStyles: {
            ...defaultRadioInputProps.customStyles,
            marginTop: '-17px',
          },
        }),
        required: true,
      },
    },
    ownershipInterestPastThreeYears: {
      fieldId: 'ownershipInterestPastThreeYears',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}ownershipInterestPastThreeYears`,
      inputProps: {
        className: 'ownership-interest-past-three-years',
        label: 'Have you had an ownership interest in another property in the last three years?',
        dataTestId:
          BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_OWNERSHIPINTERESTPASTTHREEYEARS_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    pastThreeYearsPropertyPurpose: {
      fieldId: 'pastThreeYearsPropertyPurpose',
      type: FieldType.SELECT,
      nameInModel: `${modelPath}pastThreeYearsPropertyPurpose`,
      inputProps: {
        additionalTitle: '1) What type of property did you own?',
        label: 'Property Type',
        required: true,
        options: occupancyWithSecondaryResidenceOptions,
        variant: 'outlined',
        placeholder: 'Choose marital status',
        ...getAdditionalContentProps(),
      },
    },
    titleHoldingType: {
      fieldId: 'titleHoldingType',
      type: FieldType.SELECT,
      nameInModel: `${modelPath}titleHoldingType`,
      inputProps: {
        label: 'Title of the property',
        additionalTitle: '2) How did you hold title to the property?',
        required: true,
        options: titleHoldingOptions,
        variant: 'outlined',
        placeholder: 'Choose marital status',
        ...getAdditionalContentProps(true),
      },
    },
    relationshipWithPropertySeller: {
      fieldId: 'relationshipWithPropertySeller',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}relationshipWithPropertySeller`,
      inputProps: {
        label:
          'Do you have a family relationship or business affiliation with the seller of the property?',
        dataTestId:
          BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_RELATIONSHIPWITHPROPERTYSELLER_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    partOfDownPaymentBorrowed: {
      fieldId: 'partOfDownPaymentBorrowed',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}partOfDownPaymentBorrowed`,
      inputProps: {
        label:
          'Are you borrowing any money for this real estate transaction (e.g., money for your closing costs or down payment) or\n' +
          'obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?',
        dataTestId:
          BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_PARTOFDOWNPAYMENTBORROWED_SELECT,
        ...defaultRadioInputProps,
        required: false,
      },
    },
    partOfDownPaymentBorrowedAmount: {
      fieldId: 'partOfDownPaymentBorrowedAmount',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}partOfDownPaymentBorrowedAmount`,
      inputProps: {
        placeholder: '-',
        additionalTitle: 'What is the amount of this money?',
        label: 'Amount',
        required: true,
        variant: 'outlined',
        type: FieldInputType.CURRENCY,
        dataTestId:
          BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_PARTOFDOWNPAYMENTBORROWEDAMOUNT_INPUT,
        ...getAdditionalContentProps(true),
      },
    },
    loanOnAnotherProperty: {
      fieldId: 'loanOnAnotherProperty',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}loanOnAnotherProperty`,
      inputProps: {
        label:
          'Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application? ',
        dataTestId:
          BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_LOANONANOTHERPROPERTY_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    anyNewCreditOutsideOfTheLoan: {
      fieldId: 'anyNewCreditOutsideOfTheLoan',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}anyNewCreditOutsideOfTheLoan`,
      inputProps: {
        label:
          'Have you or will you be applying for any new credit (e.g., installment loan, credit card, etc.) on or before closing this loan that\n' +
          'is not disclosed on this application?',
        dataTestId:
          BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_ANYNEWCREDITOUTSIDEOFTHELOAN_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    isPropertyLien: {
      fieldId: 'isPropertyLien',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}isPropertyLien`,
      inputProps: {
        label:
          'Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid\n' +
          'through your property taxes (e.g., the Property Assessed Clean Energy Program)?',
        dataTestId: BorrowersTestIds.DECLARATIONS_PROPERTY_AND_LOAN_FORM_ISPROPERTYLIEN_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
  };
};

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    ownershipInterestPastThreeYears,
    partOfDownPaymentBorrowed,
    isPurchaseFlow,
    isBorrowerSectionWasOpened,
  }) => {
    const propertyAndLoanConfigFields = getConfigFields({
      entityId,
      isBorrowerSectionWasOpened,
    });

    return {
      groups: [
        {
          title: { label: 'Property & Loan', titleStyles: sectionTitleStyles },
          key: `${entityId}-declarations-property-and-loan`,
          type: FieldType.GROUP,
          customStyles: sectionStyles,
          fields: [
            ...getConditionalArray(!isBorrowerSectionWasOpened, [
              propertyAndLoanConfigFields.isFirstTimeHomeBuyer,
            ]),

            propertyAndLoanConfigFields.intendToOccupyPropertyAsPrimaryResidence,

            propertyAndLoanConfigFields.ownershipInterestPastThreeYears,
            ...getConditionalArray(ownershipInterestPastThreeYears, [
              propertyAndLoanConfigFields.pastThreeYearsPropertyPurpose,
              propertyAndLoanConfigFields.titleHoldingType,
            ]),

            ...getConditionalArray(isPurchaseFlow, [
              propertyAndLoanConfigFields.relationshipWithPropertySeller,
            ]),

            propertyAndLoanConfigFields.partOfDownPaymentBorrowed,
            ...getConditionalArray(partOfDownPaymentBorrowed, [
              propertyAndLoanConfigFields.partOfDownPaymentBorrowedAmount,
            ]),

            propertyAndLoanConfigFields.loanOnAnotherProperty,
            propertyAndLoanConfigFields.anyNewCreditOutsideOfTheLoan,
            propertyAndLoanConfigFields.isPropertyLien,
          ],
        },
      ],
    };
  },
);
